import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle2, Clock, Check, AlertCircle } from 'lucide-react';
import { BakeryEvent, BookingChoice } from '../types/event';
import { INFO_EMAIL, sendBokabordMail } from '../utils/infoMail';
import { guestTexts } from '../data/guestTexts';
import { createBookingNumber, BookingStats, fetchBookingStats, fetchSlotCounts, saveEventBooking } from '../utils/eventSheet';
import { TimeSlotPicker } from './TimeSlotPicker';
import { formatPhone, validateEmail, validateName, validatePhone } from '../utils/validation';
interface EventSignupModalProps {
  event: BakeryEvent;
  onClose: () => void;
}
type Field = 'firstName' | 'lastName' | 'email' | 'phone';
const MAX_CHILDREN = 10;
const ease = [0.23, 1, 0.32, 1] as const;
export function EventSignupModal({
  event,
  onClose
}: EventSignupModalProps) {
  // Children's events: a parent ticks "Jag är förälder" and states how many
  // children they bring. Without the tick, the child signs up themselves.
  const isKidsEvent = !!event.childSignup || !!event.parentToggle;
  const [asParent, setAsParent] = useState(false);
  const isParent = isKidsEvent && asParent;
  const [childCount, setChildCount] = useState('1');
  const [childTouched, setChildTouched] = useState(false);
  const childNum = Number(childCount);
  const childError = !isParent ? null : childCount.trim() === '' ? 'Ange hur många barn du tar med.' : !Number.isInteger(childNum) || childNum < 1 ? 'Ange minst 1 barn.' : childNum > MAX_CHILDREN ? `Max ${MAX_CHILDREN} barn per anmälan – hör av dig till oss om ni är fler.` : null;
  const stepChildren = (delta: number) => {
    setChildTouched(true);
    setChildCount(c => {
      const current = c.trim() === '' || isNaN(Number(c)) ? 1 - delta : Number(c);
      return String(Math.min(MAX_CHILDREN, Math.max(1, current + delta)));
    });
  };
  const [values, setValues] = useState<Record<Field, string>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [touched, setTouched] = useState<Record<Field, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
    phone: false
  });
  // Booking limits – per event and/or per choice, counted from the sheet
  const choices = event.bookingOptions?.choices ?? [];
  const hasLimits = !!event.maxBookings || choices.some(c => c.maxBookings || c.maxPeople);
  const [stats, setStats] = useState<BookingStats | null>(null);
  useEffect(() => {
    if (!hasLimits) return;
    let active = true;
    fetchBookingStats(event.id).then(s => active && setStats(s));
    return () => {
      active = false;
    };
  }, [event.id, hasLimits]);
  const isChoiceFull = (c: BookingChoice, s: BookingStats | null = stats) => {
    if (!s) return false;
    const used = s.byChoice[c.label] ?? {
      bookings: 0,
      people: 0
    };
    return !!c.maxBookings && used.bookings >= c.maxBookings || !!c.maxPeople && used.people >= c.maxPeople;
  };
  const isEventFull = (s: BookingStats | null = stats) => !!s && (!!event.maxBookings && s.total >= event.maxBookings || choices.length > 0 && choices.some(c => c.maxBookings || c.maxPeople) && choices.every(c => isChoiceFull(c, s)));
  const [choice, setChoice] = useState<string | null>(null);
  const [choiceTouched, setChoiceTouched] = useState(false);
  const [party, setParty] = useState('');
  const [partyTouched, setPartyTouched] = useState(false);
  const selectedChoice = choices.find(c => c.label === choice);
  const choiceError = !event.bookingOptions ? null : !choice ? 'Välj ett alternativ.' : null;
  const partyCfg = selectedChoice?.partySize ?? event.partySize;
  const partyNum = Number(party);
  const partyError = !partyCfg ? null : party.trim() === '' ? 'Ange hur många ni är.' : !Number.isInteger(partyNum) ? 'Ange ett helt antal personer.' : partyNum < partyCfg.min ? partyCfg.minMessage ?? `Ni behöver vara minst ${partyCfg.min} personer för att boka.` : partyNum > partyCfg.max ? `Max ${partyCfg.max} personer per bokning – hör av dig till oss för större sällskap.` : null;
  const stepParty = (delta: number) => {
    setPartyTouched(true);
    setParty(p => {
      if (!partyCfg) return p;
      const current = p.trim() === '' || isNaN(Number(p)) ? partyCfg.min - delta : Number(p);
      return String(Math.min(partyCfg.max, Math.max(partyCfg.min, current + delta)));
    });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  // Time slots with a seat limit – overflow goes to the waitlist
  const slots = event.timeSlots;
  const [slot, setSlot] = useState<string | null>(null);
  const [slotTouched, setSlotTouched] = useState(false);
  const [slotCounts, setSlotCounts] = useState<Record<string, number> | null | undefined>(undefined);
  useEffect(() => {
    if (!slots) return;
    let active = true;
    fetchSlotCounts(event.id).then(c => active && setSlotCounts(c));
    return () => {
      active = false;
    };
  }, [event.id, slots]);
  const slotError = slots && !slot ? 'Välj en tid.' : null;
  const seatsLeft = slots && slot && slotCounts ? Math.max(0, slots.capacity - (slotCounts[slot] ?? 0)) : null;
  // Capacity is a hard ceiling: a booking goes through only if the whole
  // party fits. Otherwise it goes to the waitlist.
  const partyForSlot = partyCfg && !partyError ? partyNum : 1;
  const waitlisted = seatsLeft !== null && partyForSlot > seatsLeft;
  // Booking limits per event/choice – when reached, bookings go to the waitlist
  const limitWaitlisted = (s: BookingStats | null) => {
    if (!s) return false;
    if (isEventFull(s)) return true;
    if (!selectedChoice) return false;
    if (isChoiceFull(selectedChoice, s)) return true;
    if (selectedChoice.maxPeople && partyCfg && !partyError) {
      const left = selectedChoice.maxPeople - (s.byChoice[selectedChoice.label]?.people ?? 0);
      return partyNum > left;
    }
    return false;
  };
  const limitWaitlist = hasLimits && limitWaitlisted(stats);
  const slotStatus = slots ? slotCounts === null ? 'Ej kontrollerad' : waitlisted ? 'Väntelista' : 'Bekräftad' : hasLimits ? stats === null ? 'Ej kontrollerad' : limitWaitlist ? 'Väntelista' : 'Bekräftad' : undefined;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const errors: Record<Field, string | null> = {
    firstName: validateName(values.firstName, 'förnamn'),
    lastName: validateName(values.lastName, 'efternamn'),
    email: validateEmail(values.email),
    phone: validatePhone(values.phone)
  };
  const isValid = Object.values(errors).every(e => e === null);
  const dateLabel = new Date(`${event.date}T12:00:00`).toLocaleDateString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const firstName = values.firstName.trim();
  const lastName = values.lastName.trim();
  const fullName = `${firstName} ${lastName}`;
  const childText = `${childNum} barn`;
  const setField = (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => setValues(v => ({
    ...v,
    [field]: e.target.value
  }));
  const touch = (field: Field) => () => setTouched(t => ({
    ...t,
    [field]: true
  }));
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setChildTouched(true);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true
    });
    setChoiceTouched(true);
    setPartyTouched(true);
    setSlotTouched(true);
    if (slotError) {
      document.getElementById('su-slot-0')?.focus();
      return;
    }
    if (choiceError) {
      document.getElementById('su-choice-0')?.focus();
      if (isValid) return;
    }
    if (partyError) {
      document.getElementById('su-party')?.focus();
      if (isValid) return;
    }
    if (childError) {
      document.getElementById('su-children')?.focus();
      return;
    }
    if (!isValid) {
      const firstInvalid = (Object.keys(errors) as Field[]).find(f => errors[f]);
      if (firstInvalid) document.getElementById(`su-${firstInvalid}`)?.focus();
      return;
    }
    setIsSubmitting(true);
    // Re-check the limit right before booking, in case it filled up meanwhile
    if (hasLimits) {
      const latest = await fetchBookingStats(event.id);
      if (latest) {
        setStats(latest);
        // It filled up while the form was open – stop so the guest sees the
        // waitlist info and confirms with "Ställ mig på väntelistan".
        if (slotStatus !== 'Väntelista' && limitWaitlisted(latest)) {
          setIsSubmitting(false);
          return;
        }
      }
    }
    const email = values.email.trim();
    const phone = formatPhone(values.phone);
    const timeText = slot ?? selectedChoice?.time ?? `${event.time ?? 'Ej angiven'}${event.secondaryTime ? ` (${event.secondaryTime})` : ''}`;
    const bookingNumber = createBookingNumber(event.id);
    const choiceLabel = ({
      'halloween-run-club': 'DELTAR I:  ',
      'halloween-hidden-reality': 'SPELFORM:  ',
      'halloween-hostbrunch': 'SITTNING:  '
    } as Record<string, string>)[event.id] ?? 'VAL:       ';
    const message = [`════════════════════════════════`, slotStatus === 'Väntelista' ? `   VÄNTELISTA – HALLOWEEK` : `   NY BOKNING – HALLOWEEK`, `   Gäller: ${event.title}`, `   ${bookingNumber}`, `════════════════════════════════`, '', `▸ DEL:        Halloweek (vecka 44)`, `▸ EVENT:      ${event.title}`, `▸ DATUM:      ${event.date} (${dateLabel})`, `▸ TID:        ${timeText}`, ...(slotStatus ? [`▸ STATUS:     ${slotStatus.toUpperCase()}`] : []), ...(choice ? [`▸ ${choiceLabel} ${choice.toUpperCase()}`] : []), ...(partyCfg ? [`▸ SÄLLSKAP:   ${partyNum} personer`] : []), '', ...(isKidsEvent ? [`▸ ANMÄLD AV:  ${isParent ? 'FÖRÄLDER' : 'BARNET SJÄLV'}`, `▸ ANTAL BARN: ${isParent ? childNum : 1}`, ''] : []), `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, isParent ? `   FÖRÄLDER / KONTAKTPERSON` : `   DELTAGARE`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  Namn:      ${fullName}`, `  E-post:    ${email}`, `  Telefon:   ${phone}`, '', `════════════════════════════════`, isParent ? `Svara direkt på detta mejl för att nå föräldern.` : `Svara direkt på detta mejl för att nå deltagaren.`, `════════════════════════════════`].join('\n');

    // Sheet and email run independently – one failing never blocks the other
    const mail = {
      subject: `[HALLOWEEK · ${event.title}${slotStatus === 'Väntelista' ? ' · VÄNTELISTA' : ''}] ${event.date}${slot ? ` kl ${slot}` : ''}${choice ? ` · ${choice}` : ''}${partyCfg ? ` – ${partyNum} pers` : ''} – ${isParent ? `Förälder: ${fullName} – ${childText}` : isKidsEvent ? `${fullName} (anmäler sig själv)` : fullName} (${bookingNumber})`,
      message,
      fromName: `Top Notch Halloweek – ${fullName}`,
      replyTo: email,
      name: fullName,
      gasttext: guestTexts[event.id] ?? '',
      bokning: [event.title, `${dateLabel}${timeText && timeText !== 'Ej angiven' ? ` kl. ${timeText}` : ''}`, ...(choice ? [choice] : []), ...(partyCfg ? [`${partyNum} ${partyNum === 1 ? 'person' : 'personer'}`] : []), ...(isKidsEvent ? [`${isParent ? childNum : 1} barn`] : []), ...(slotStatus === 'Väntelista' ? ['Väntelista'] : [])].join(' · ')
    };
    const sheetBase = {
      eventId: event.id,
      bookingNumber,
      date: event.date,
      time: timeText,
      choice,
      partySize: partyCfg ? partyNum : null,
      firstName,
      lastName,
      email,
      phone,
      registeredBy: isKidsEvent ? isParent ? 'Förälder' : 'Barnet själv' : undefined,
      childCount: isKidsEvent ? isParent ? childNum : 1 : undefined,
      status: slotStatus
    };
    const [sheetOk, bokabordOk] = await Promise.all([saveEventBooking(sheetBase), sendBokabordMail(mail)]);
    if (!sheetOk) console.error('SheetDB: bokningen kunde inte sparas');
    if (!bokabordOk) console.error('Web3Forms: mejlet till bokabord@ kunde inte skickas');
    const ok = sheetOk || bokabordOk;
    setIsSubmitting(false);
    if (ok) {
      setSent(true);
    } else {
      setError(`Något gick fel. Försök igen eller mejla oss på ${INFO_EMAIL}.`);
    }
  };
  const renderField = (field: Field, label: string, props: React.InputHTMLAttributes<HTMLInputElement>) => {
    const showError = touched[field] && errors[field];
    const showValid = touched[field] && !errors[field];
    return <div data-id="element-509" data-mp="1aj1naxks5ecf">
        <label htmlFor={`su-${field}`} className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-510" data-mp="1airvr5k36z4m">
          {label}
        </label>
        <div className="relative" data-id="element-511" data-mp="dqod2bfg25db">
          <input id={`su-${field}`} value={values[field]} onChange={setField(field)} onBlur={touch(field)} aria-invalid={!!showError} aria-describedby={showError ? `su-${field}-error` : undefined} className={`w-full h-11 pl-3 pr-9 rounded-lg bg-[#0E0C0B] text-[#F5EDE3] placeholder:text-[#F5EDE3]/30 text-sm outline-none border transition-[border-color,box-shadow] duration-150 focus:ring-2 ${showError ? 'border-[#F87171] focus:ring-[#F87171]/25' : 'border-[#F5EDE3]/15 focus:border-[#FF7A1A] focus:ring-[#FF7A1A]/25'}`} {...props} data-id="element-512" data-mp="ircbvj9wzqi3" />
          {showValid && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF9A4D]" aria-hidden="true" data-id="element-513" data-mp="3lv8su9gu8u3u" />}
          {showError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F87171]" aria-hidden="true" data-id="element-514" data-mp="3uu3ur9cuni1c" />}
        </div>
        {showError && <p id={`su-${field}-error`} className="mt-1.5 text-xs font-medium text-[#FCA5A5]" data-id="element-515" data-mp="8nf4zh43xng6">
            {errors[field]}
          </p>}
      </div>;
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.2,
    ease
  }} className="halloween-theme fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-id="element-516" data-mp="2afk35ac79gzc">
      <motion.div role="dialog" aria-modal="true" aria-labelledby="event-signup-title" initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease
    }} onClick={e => e.stopPropagation()} className="bg-[#1C1917] text-[#F5EDE3] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden my-8 border-t-4 border-[#FF7A1A]" data-id="element-517" data-mp="pwg9q6sjejar">
        <div className="px-6 pt-6 pb-5 border-b border-[#F5EDE3]/10 flex items-start justify-between gap-4" data-id="element-518" data-mp="3ofesw62c00f4">
          <div className="min-w-0" data-id="element-519" data-mp="3td6opb63k6ol">
            <p className="text-xs font-bold text-[#FF9A4D] mb-1.5" data-id="element-520" data-mp="3te89469065iz">
              Boka in dig · Halloweek
            </p>
            <h2 id="event-signup-title" className="text-2xl font-bold leading-tight" data-id="element-521" data-mp="1mlp9jdtte0ar">
              {event.title}
            </h2>
            <p className="mt-2 text-sm text-[#F5EDE3]/60 flex items-center gap-1.5" data-id="element-522" data-mp="1q6r5om72fftd">
              <Clock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" data-id="element-523" data-mp="1l5ju0c1sxwhf" />
              <span className="capitalize" data-id="element-524" data-mp="1g6gs30h4c6do">{dateLabel}</span>
              {event.time ? ` · ${event.time}` : ''}
            </p>
            <p className="mt-1 text-xs font-semibold text-[#FFB27A]" data-id="element-525" data-mp="1b9qd6gh1vgtu">
              Begränsat antal platser – boka i tid!
            </p>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 -mt-1 text-[#F5EDE3]/50 hover:text-[#F5EDE3] hover:bg-[#F5EDE3]/5 rounded-full transition-colors shrink-0" aria-label="Stäng" data-id="element-526" data-mp="23x49qdgu6adf">
            <X className="w-5 h-5" data-id="element-527" data-mp="1u69pfen5hkum" />
          </button>
        </div>

        <div className="p-6" data-id="element-528" data-mp="2rmyo4zgt06zs">
          {!sent ? <form onSubmit={handleSubmit} noValidate className="space-y-4" data-id="element-529" data-mp="2rnq9lhgosocc">
              {slots && <TimeSlotPicker times={slots.times} capacity={slots.capacity} counts={slotCounts} selected={slot} onSelect={setSlot} error={slotTouched ? slotError : null} data-id="element-530" data-mp="2mtlzwqg1e27k" />}
              {event.bookingOptions && <fieldset aria-describedby={choiceTouched && choiceError ? 'su-choice-error' : undefined} data-id="element-531" data-mp="394dwb1ftkvph">
                  <legend className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-532" data-mp="3i4afwoncr4do">
                    {event.bookingOptions.question} *
                  </legend>
                  <div className="space-y-2" data-id="element-533" data-mp="3mxv82029l44y">
                    {event.bookingOptions.choices.map((c, i) => {
                const selected = choice === c.label;
                const full = isChoiceFull(c);
                return <label key={c.label} className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors duration-150 ${selected ? 'border-[#FF7A1A] bg-[#FF7A1A]/10' : choiceTouched && choiceError ? 'border-[#F87171] bg-[#0E0C0B]' : 'border-[#F5EDE3]/15 bg-[#0E0C0B] hover:border-[#F5EDE3]/35'}`} data-id="element-534" data-mp="5o6xb88cnmn1" data-mpi={i}>
                          <input id={`su-choice-${i}`} type="radio" name="booking-choice" value={c.label} checked={selected} onChange={() => setChoice(c.label)} className="sr-only peer" data-id="element-535" data-mp="39sz5idcz0pw" />
                          <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-[#FF7A1A]/50 ${selected ? 'bg-[#FF7A1A] border-[#FF7A1A]' : 'border-[#F5EDE3]/40'}`} aria-hidden="true" data-id="element-536" data-mp="3fpeli25p848m">
                            {selected && <Check className="w-3.5 h-3.5 text-[#1A0F08]" strokeWidth={3} data-id="element-537" data-mp="2iy87t0slwiqf" />}
                          </span>
                          <span className="min-w-0" data-id="element-538" data-mp="29v71ayztwpm8">
                            <span className="block text-sm font-bold text-[#F5EDE3]" data-id="element-539" data-mp="ti8r8ymnrr0r">
                              {c.label}
                            </span>
                            {full && <span className="block text-xs font-bold text-[#FF9A4D] mt-0.5" data-id="element-540" data-mp="18qqsofwe0d00">
                                Fullbokat · väntelista
                              </span>}
                            {c.description && <span className="block text-xs text-[#F5EDE3]/55 mt-0.5" data-id="element-541" data-mp="zswzhdtx5zh2">
                                {c.description}
                              </span>}
                          </span>
                        </label>;
              })}
                  </div>
                  {choiceTouched && choiceError && <p id="su-choice-error" className="mt-1.5 text-xs font-medium text-[#FCA5A5]" data-id="element-542" data-mp="g0ibaq9odwmh">
                      {choiceError}
                    </p>}
                </fieldset>}
              {partyCfg && <div data-id="element-543" data-mp="c2t1pn3ewtxz">
                  <label htmlFor="su-party" className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-544" data-mp="3ok2eoldwq8ww">
                    {partyCfg.label} *
                  </label>
                  <div className="flex items-center gap-3" data-id="element-545" data-mp="1go04r1k5oes">
                    <div className={`flex items-center h-12 rounded-lg bg-[#0E0C0B] border transition-[border-color] duration-150 ${partyTouched && partyError ? 'border-[#F87171]' : 'border-[#F5EDE3]/15 focus-within:border-[#FF7A1A]'}`} data-id="element-546" data-mp="3tj5mgdl9ut8k">
                      <button type="button" onClick={() => stepParty(-1)} disabled={party !== '' && partyNum <= partyCfg.min} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="En person färre" data-id="element-547" data-mp="g4shnrovjtvz">
                        −
                      </button>
                      <input id="su-party" type="number" inputMode="numeric" min={partyCfg.min} max={partyCfg.max} step={1} value={party} placeholder={String(partyCfg.min)} onChange={e => setParty(e.target.value)} onBlur={() => setPartyTouched(true)} aria-invalid={!!(partyTouched && partyError)} aria-describedby="su-party-hint" className="w-14 h-full bg-transparent text-center text-lg font-bold text-[#F5EDE3] placeholder:text-[#F5EDE3]/25 outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-id="element-548" data-mp="3rydehxx6dshp" />
                      <button type="button" onClick={() => stepParty(1)} disabled={party !== '' && partyNum >= partyCfg.max} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="En person till" data-id="element-549" data-mp="28z761nn49thp">
                        +
                      </button>
                    </div>
                    <span className="text-sm text-[#F5EDE3]/60" data-id="element-550" data-mp="2nmidbdc9mrc0">personer</span>
                  </div>
                  <p id="su-party-hint" className={`mt-1.5 text-xs font-medium ${partyTouched && partyError ? 'text-[#FCA5A5]' : 'text-[#FF9A4D]'}`} data-id="element-551" data-mp="2ioz0mhv7umm6">
                    {partyTouched && partyError ? partyError : partyCfg.hint}
                  </p>
                </div>}
              {slot && waitlisted && <p role="status" className="rounded-lg bg-[#FF7A1A]/10 border border-[#FF7A1A]/30 px-3 py-2.5 text-sm text-[#F5EDE3]/90 leading-relaxed" data-id="element-552" data-mp="2m7ebnfvxw7xr">
                  <span className="font-bold text-[#FF9A4D]" data-id="element-553" data-mp="3dxskuyt0cw33">
                    {seatsLeft === 0 ? `Kl ${slot} är fullbokad.` : `Kl ${slot} har inte plats för hela ert sällskap.`}
                  </span>{' '}
                  Du kan ställa er på väntelistan – vi hör av oss om det blir
                  plats – eller välja en annan tid. Ni är också alltid
                  välkomna som drop in.
                </p>}
              {!slots && limitWaitlist && <p role="status" className="rounded-lg bg-[#FF7A1A]/10 border border-[#FF7A1A]/30 px-3 py-2.5 text-sm text-[#F5EDE3]/90 leading-relaxed" data-id="element-554" data-mp="1yefp5lfdyvfg">
                  <span className="font-bold text-[#FF9A4D]" data-id="element-555" data-mp="1m096ck5oulcz">
                    {selectedChoice && isChoiceFull(selectedChoice) ? `${selectedChoice.label} är fullbokat.` : selectedChoice ? `${selectedChoice.label} har inte plats för hela ert sällskap.` : `${event.title} är fullbokat.`}
                  </span>{' '}
                  Du kan ställa dig på väntelistan – vi hör av oss om det blir
                  plats.
                  {choices.length > 1 && ' Eller välj ett annat alternativ.'}
                </p>}
              {selectedChoice?.note && <p className="rounded-lg bg-[#FF7A1A]/10 border border-[#FF7A1A]/30 px-3 py-2.5 text-sm text-[#F5EDE3]/90 leading-relaxed" data-id="element-556" data-mp="ogy3l9ocs1uc">
                  {selectedChoice.note}
                </p>}
              {isKidsEvent && <div className="space-y-3" data-id="element-557" data-mp="2okiij0ggzi5z">
                  <button type="button" role="checkbox" aria-checked={asParent} onClick={() => setAsParent(v => !v)} className={`w-full flex items-start gap-3 text-left rounded-xl border px-3 py-3 transition-colors ${asParent ? 'border-[#FF7A1A] bg-[#FF7A1A]/10' : 'border-[#F5EDE3]/15 hover:border-[#F5EDE3]/35'}`} data-id="element-558" data-mp="2oj7csv3gs0c8">
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${asParent ? 'bg-[#FF7A1A] border-[#FF7A1A]' : 'border-[#F5EDE3]/40'}`} aria-hidden="true" data-id="element-559" data-mp="29pudfnezelqw">
                      {asParent && <Check className="w-3.5 h-3.5 text-[#1A0F08]" data-id="element-560" data-mp="3z8vxiocgb73" />}
                    </span>
                    <span data-id="element-561" data-mp="3v7mrab1jhr8y">
                      <span className="block text-sm font-bold text-[#F5EDE3]" data-id="element-562" data-mp="3v7d9haqneqec">
                        Jag är förälder
                      </span>
                      <span className="block text-xs text-[#F5EDE3]/60 leading-relaxed" data-id="element-563" data-mp="3met7c0b5e3or">
                        Bocka i för att anmäla dina barn – du anger hur många
                        du tar med. Anmäler sig barnet självt, lämna rutan tom.
                      </span>
                    </span>
                  </button>

                  {isParent && <div data-id="element-564" data-mp="37j3kjzy51miz">
                      <label htmlFor="su-children" className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-565" data-mp="33fp1f0ewkq1b">
                        Hur många barn tar du med? *
                      </label>
                      <div className="flex items-center gap-3" data-id="element-566" data-mp="11ly7aj4xs05m">
                        <div className={`flex items-center h-12 rounded-lg bg-[#0E0C0B] border transition-[border-color] duration-150 ${childTouched && childError ? 'border-[#F87171]' : 'border-[#F5EDE3]/15 focus-within:border-[#FF7A1A]'}`} data-id="element-567" data-mp="1ky3mxhnt265v">
                          <button type="button" onClick={() => stepChildren(-1)} disabled={childNum <= 1} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="Ett barn färre" data-id="element-568" data-mp="1179u2lxp9qa7">
                            −
                          </button>
                          <input id="su-children" type="number" inputMode="numeric" min={1} max={MAX_CHILDREN} step={1} value={childCount} onChange={e => setChildCount(e.target.value)} onBlur={() => setChildTouched(true)} aria-invalid={!!(childTouched && childError)} aria-describedby="su-children-hint" className="w-14 h-full bg-transparent text-center text-lg font-bold text-[#F5EDE3] outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-id="element-569" data-mp="2rj690zslsv0u" />
                          <button type="button" onClick={() => stepChildren(1)} disabled={childNum >= MAX_CHILDREN} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="Ett barn till" data-id="element-570" data-mp="1z4f3bdbe5wme">
                            +
                          </button>
                        </div>
                        <span className="text-sm text-[#F5EDE3]/60" data-id="element-571" data-mp="l9ho6h659msi">barn</span>
                      </div>
                      <p id="su-children-hint" className={`mt-1.5 text-xs font-medium ${childTouched && childError ? 'text-[#FCA5A5]' : 'text-[#F5EDE3]/55'}`} data-id="element-572" data-mp="g8bxeow1o9lm">
                        {childTouched && childError ? childError : 'Anmälan görs i ditt namn – fyll i dina uppgifter nedan.'}
                      </p>
                    </div>}
                </div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-573" data-mp="2d7kk8bf7cjw4">
                {renderField('firstName', isParent ? 'Förälderns förnamn *' : 'Förnamn *', {
              type: 'text',
              autoComplete: 'given-name',
              maxLength: 40
            })}
                {renderField('lastName', isParent ? 'Förälderns efternamn *' : 'Efternamn *', {
              type: 'text',
              autoComplete: 'family-name',
              maxLength: 40
            })}
              </div>
              {renderField('email', 'E-post *', {
            type: 'email',
            autoComplete: 'email',
            inputMode: 'email',
            placeholder: 'namn@exempel.se'
          })}
              {renderField('phone', 'Telefon *', {
            type: 'tel',
            autoComplete: 'tel',
            inputMode: 'tel',
            placeholder: '070-123 45 67'
          })}

              {event.formNote && <p className="rounded-lg bg-[#FF7A1A]/10 border border-[#FF7A1A]/30 px-3 py-2.5 text-sm text-[#F5EDE3]/90 leading-relaxed" data-id="element-574" data-mp="2ztz5nlwdagpq">
                  {event.formNote}
                </p>}

              {error && <p role="alert" className="p-3 bg-[#F87171]/10 text-[#FCA5A5] text-sm rounded-lg border border-[#F87171]/30" data-id="element-575" data-mp="229cnfec85105">
                  {error}
                </p>}

              <div className="pt-2" data-id="element-576" data-mp="2c5fv0zpuhruh">
                <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#FF7A1A] text-[#1A0F08] font-bold hover:bg-[#FF9A4D] disabled:opacity-60 disabled:cursor-wait transition-colors" data-id="element-577" data-mp="1ogwr0wdtw187">
                  {isSubmitting && <span className="w-4 h-4 rounded-full border-2 border-[#1A0F08]/30 border-t-[#1A0F08] animate-spin" aria-hidden="true" data-id="element-578" data-mp="3skkfz3y2ilt" />}
                  {isSubmitting ? 'Skickar…' : slotStatus === 'Väntelista' ? 'Ställ mig på väntelistan' : event.ctaLabel ?? 'Boka min plats'}
                </button>
                <p className="text-center text-xs text-[#F5EDE3]/45 mt-3" data-id="element-579" data-mp="8pkpq7syzhbv">
                  {slotStatus === 'Väntelista' ? 'Vi mejlar dig om en plats blir ledig.' : 'Vi bekräftar din plats via mejl.'}
                </p>
              </div>
            </form> : <div className="text-center py-6 space-y-4" data-id="element-580" data-mp="2xfk8zt1thlhb">
              <div className="w-14 h-14 bg-[#FF7A1A]/15 rounded-full flex items-center justify-center mx-auto" data-id="element-581" data-mp="3dpuqt6aydk2m">
                <CheckCircle2 className="w-7 h-7 text-[#FF7A1A]" data-id="element-582" data-mp="38xb5to9xrmns" />
              </div>
              <h3 className="text-2xl font-bold" data-id="element-583" data-mp="3401gs9qgld0h">Tack, {firstName}!</h3>
              <p className="text-[#F5EDE3]/70 max-w-sm mx-auto text-sm leading-relaxed" data-id="element-584" data-mp="2z06n5yfau9bq">
                {slotStatus === 'Väntelista' ? slot ? `Du står nu på väntelistan för ${event.title} kl ${slot}. Vi hör av oss på ${values.email.trim()} om det blir en ledig plats. Du är också varmt välkommen som drop in!` : `Du står nu på väntelistan för ${event.title}${choice ? ` (${choice.toLowerCase()})` : ''}. Vi hör av oss på ${values.email.trim()} om det blir en ledig plats.` : isParent ? `Vi har tagit emot din anmälan av ${childText} till ${event.title}. Du får en bekräftelse från oss på ${values.email.trim()}.` : `Vi har tagit emot din bokning till ${event.title}. Du får en bekräftelse från oss på ${values.email.trim()}.`}
              </p>
              <div className="pt-4" data-id="element-585" data-mp="qsjsgzb07z4k">
                <button onClick={onClose} className="px-8 py-3 rounded-full border border-[#F5EDE3]/40 text-sm font-bold hover:bg-[#F5EDE3] hover:text-[#0E0C0B] transition-colors" data-id="element-586" data-mp="vrmusbzlnr8u">
                  Stäng
                </button>
              </div>
            </div>}
        </div>
      </motion.div>
    </motion.div>;
}